import request from './request';

const PLATFORM_TOKEN_KEY = 'converge.token.private';
const PREMIO_ADMIN_TOKEN_URL = 'integrations/premio/admin-token';
const REFRESH_SKEW_MS = 30 * 1000;

let cachedToken = null;
let cachedTokenExpiresAt = 0;
let cachedPlatformToken = null;
let pendingTokenRequest = null;
let pendingPlatformToken = null;

const getJwtExpiresAt = (token) => {
  try {
    const payload = token.split('.')[1];

    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalizedPayload.length % 4)) % 4);
    const claims = JSON.parse(window.atob(`${normalizedPayload}${padding}`));

    return Number.isFinite(claims.exp) ? claims.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const getTokenExpiresAt = (token, expiresIn, now) => {
  const expiresInSeconds = Number(expiresIn);
  const ttlExpiresAt =
    Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
      ? now + expiresInSeconds * 1000
      : null;
  const jwtExpiresAt = getJwtExpiresAt(token);

  if (ttlExpiresAt && jwtExpiresAt) {
    return Math.min(ttlExpiresAt, jwtExpiresAt);
  }

  return ttlExpiresAt || jwtExpiresAt || now;
};

const hasValidCachedToken = (platformToken) =>
  Boolean(cachedToken) &&
  cachedPlatformToken === platformToken &&
  Date.now() + REFRESH_SKEW_MS < cachedTokenExpiresAt;

const requestPremioAdminToken = (platformToken) => {
  return request({
    url: PREMIO_ADMIN_TOKEN_URL,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${platformToken}`,
    },
  }).then((response) => {
    const { accessToken, expiresIn } = response.data || {};

    if (!accessToken) {
      throw new Error('La plataforma no entregó un token de acceso a Premio');
    }

    if (window.sessionStorage.getItem(PLATFORM_TOKEN_KEY) !== platformToken) {
      throw new Error('La sesión de plataforma cambió mientras se autorizaba Premio');
    }

    const now = Date.now();
    cachedToken = accessToken;
    cachedTokenExpiresAt = getTokenExpiresAt(accessToken, expiresIn, now);
    cachedPlatformToken = platformToken;

    return cachedToken;
  });
};

const getPremioAdminToken = () => {
  const platformToken = window.sessionStorage.getItem(PLATFORM_TOKEN_KEY);

  if (!platformToken) {
    return Promise.reject(
      new Error('No hay una sesión de plataforma disponible para acceder a Premio')
    );
  }

  if (hasValidCachedToken(platformToken)) {
    return Promise.resolve(cachedToken);
  }

  if (pendingTokenRequest && pendingPlatformToken === platformToken) {
    return pendingTokenRequest;
  }

  const tokenRequest = requestPremioAdminToken(platformToken).finally(() => {
    if (pendingTokenRequest === tokenRequest) {
      pendingTokenRequest = null;
      pendingPlatformToken = null;
    }
  });
  pendingTokenRequest = tokenRequest;
  pendingPlatformToken = platformToken;

  return pendingTokenRequest;
};

const invalidatePremioAdminToken = (token) => {
  if (!token || cachedToken === token) {
    cachedToken = null;
    cachedTokenExpiresAt = 0;
    cachedPlatformToken = null;
  }
};

export { getPremioAdminToken, invalidatePremioAdminToken };
